import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, TrendingUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePublicationSearch } from '@/hooks/usePublicationSearch';
import { useSearchSuggestions } from '@/hooks/useSearchSuggestions';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { useLocalizations } from '@/hooks/useLocalizations';
import { useLocale } from '@/contexts/LocaleContext';
import { LCommon } from '@/components/i18n';
import { INTERACTION_STATES, TRANSITIONS, Z_INDEX } from '@/design-system';
import { cn } from '@/lib/utils';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;
const TITLE_SUGGESTION_LIMIT = 8;

/**
 * Top-bar search: a magnifier button that expands into a full search form.
 * While the user types, matching publication titles are listed Google-style;
 * clicking one navigates straight to the publication. When the field is
 * empty or too short, popular/trending suggestions are shown instead.
 */
export const HeaderSearch = (): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { lCommon } = useLocalizations();
  const { settings } = useLocale();

  // Debounce the query so we don't fire a search per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const isSearchMode = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;

  const { data: searchResults, isFetching: isSearching } = usePublicationSearch(
    debouncedQuery,
    settings.locale,
    { enabled: open && isSearchMode, limit: TITLE_SUGGESTION_LIMIT, offset: 0 },
  );
  const { data: popularSuggestions } = useSearchSuggestions(6);
  const { trackSearchSubmit, trackPublicationSearchClick } = useAnalyticsTracking();
  const lastTrackedQuery = useRef<string>('');

  // Log each completed search once per distinct query (demand dataset).
  useEffect(() => {
    if (!isSearchMode || isSearching || !searchResults) return;
    const normalized = debouncedQuery.trim().toLowerCase();
    if (normalized === lastTrackedQuery.current) return;
    lastTrackedQuery.current = normalized;
    trackSearchSubmit(normalized, searchResults.total ?? searchResults.results.length, {
      source: 'header',
      locale: settings.locale,
    });
  }, [isSearchMode, isSearching, searchResults, debouncedQuery, settings.locale, trackSearchSubmit]);

  const close = useCallback((): void => {
    setOpen(false);
    setQuery('');
    setDebouncedQuery('');
    lastTrackedQuery.current = '';
  }, []);

  // Focus the input when the form opens.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Close on Escape and on outside click.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close();
    };
    const onPointerDown = (event: PointerEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open, close]);

  const goToPublication = useCallback((publicationId: string, slug: string, translatedSlug: string | undefined, position: number): void => {
    trackPublicationSearchClick(publicationId, position, debouncedQuery.trim().toLowerCase());
    close();
    navigate(`/a/${translatedSlug || slug}`);
  }, [close, navigate, debouncedQuery, trackPublicationSearchClick]);

  const runSuggestionSearch = useCallback((text: string): void => {
    setQuery(text);
    setDebouncedQuery(text);
    inputRef.current?.focus();
  }, []);

  const titleResults = isSearchMode ? (searchResults?.results ?? []) : [];
  const showPopular = !isSearchMode && (popularSuggestions?.length ?? 0) > 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex h-10 shrink-0 items-center justify-end',
        TRANSITIONS.allSlow,
        open
          ? 'w-[min(12rem,calc(100vw-12rem))] sm:w-64 md:w-80'
          : 'w-10',
      )}
    >
      {!open && (
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-10 w-10', INTERACTION_STATES.navButton.hover)}
          onClick={() => setOpen(true)}
          aria-label={lCommon('search.open', { defaultValue: 'Search' })}
        >
          <Search className="h-5 w-5" />
        </Button>
      )}

      {open && (
        <div className="relative w-full">
          <div className="flex h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-2 shadow-sm">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={lCommon('search.placeholder', { defaultValue: 'Search publications' })}
              maxLength={120}
               className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              aria-label={lCommon('search.placeholder', { defaultValue: 'Search publications' })}
            />
            {isSearching && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={close}
              aria-label={lCommon('actions.close', { defaultValue: 'Close' })}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {(titleResults.length > 0 || showPopular) && (
            <ul
              className={cn(
                'absolute right-0 top-full mt-1 max-h-[60vh] w-full min-w-64 overflow-y-auto rounded-md border border-border bg-background py-1 shadow-md',
                Z_INDEX.dropdown,
              )}
              role="listbox"
            >
              {titleResults.map((publication, index) => (
                <li key={publication.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-muted"
                    onClick={() => goToPublication(publication.id, publication.slug, publication.translated_slug, index + 1)}
                  >
                    <span className="line-clamp-2 break-words">{publication.translated_title ?? publication.title}</span>
                  </button>
                </li>
              ))}
              {showPopular && (popularSuggestions ?? []).map((suggestion) => (
                <li key={suggestion.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                    onClick={() => runSuggestionSearch(suggestion.suggestion_text)}
                  >
                    <TrendingUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate">{suggestion.suggestion_text}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {isSearchMode && !isSearching && titleResults.length === 0 && (
            <p className={cn(
              'absolute right-0 top-full mt-1 w-full min-w-64 rounded-md border border-border bg-background px-3 py-3 text-sm text-muted-foreground shadow-md',
              Z_INDEX.dropdown,
            )}>
              <LCommon id="search.no_results" defaultValue="No publications found." />
            </p>
          )}
        </div>
      )}
    </div>
  );
};
